import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateHito } from '@/api/clientes';
import type { HitoEstado } from '@/types';

interface PendingChange {
  hitoId: string;
  hitoTitulo: string;
  prevEstado: HitoEstado;
  nextEstado: HitoEstado;
}

/**
 * R3 — Hook unificado: TODO cambio de estado abre el modal
 * (comentario obligatorio, URL opcional). El modal es genérico
 * y se adapta al estado destino.
 *
 * `extraInvalidateKeys` permite invalidar queries adicionales (ej: lista
 * consolidada del cliente cuando se cambia un estado desde el quick-view).
 */
export function useEstadoChange(
  propuestaId: string,
  extraInvalidateKeys: readonly unknown[][] = [],
) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<PendingChange | null>(null);

  const mutation = useMutation({
    mutationFn: (vars: {
      id: string;
      estado: HitoEstado;
      comentario: string;
      evidenciaUrl?: string;
    }) =>
      updateHito(vars.id, {
        estado: vars.estado,
        comentario: vars.comentario,
        evidenciaUrl: vars.evidenciaUrl,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['propuesta-detail', propuestaId] });
      qc.invalidateQueries({ queryKey: ['hito-eventos', vars.id] });
      for (const key of extraInvalidateKeys) {
        qc.invalidateQueries({ queryKey: key });
      }
    },
  });

  /** Inicia un cambio de estado: si es distinto al actual, abre modal. */
  function change(input: PendingChange) {
    if (input.nextEstado === input.prevEstado) return;
    setPending(input);
  }

  function submit(payload: { comentario: string; evidenciaUrl?: string }) {
    if (!pending) return;
    mutation.mutate(
      {
        id: pending.hitoId,
        estado: pending.nextEstado,
        comentario: payload.comentario,
        evidenciaUrl: payload.evidenciaUrl,
      },
      { onSettled: () => setPending(null) },
    );
  }

  function cancel() {
    setPending(null);
  }

  return {
    change,
    pending,
    submit,
    cancel,
    isSubmitting: mutation.isPending,
  };
}
